"use client";

import HCaptcha from "@hcaptcha/react-hcaptcha";
import {
	type FormEvent,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { GlassButton } from "@/components/design-system/buttons/GlassButton";
import { GlassIconButton } from "@/components/design-system/buttons/GlassIconButton";
import { GlassCard } from "@/components/design-system/cards/GlassCard";
import { ArtistIcon } from "@/components/design-system/icons/ArtistIcon";
import { GlassTextField } from "@/components/design-system/inputs/GlassTextField";
import { Callout } from "@/components/design-system/states/Callout";
import { useArtistRuntimeConfiguration } from "@/components/runtime/ArtistRuntimeConfigurationProvider";
import {
	type ArtistDomainLoader,
	type ArtistDomainRuntime,
	loadArtistDomain,
} from "@/domain/artistDomainBoundary";
import type { ArtistAuthProviderKey } from "@/domain/auth/AuthProviderConfiguration";
import { useTranslation, type TranslationKey } from "@/i18n/ArtistI18n";
import Link from "@/infrastructure/router/Link";
import { useRouter } from "@/infrastructure/router/navigation";
import { staticAssetPath } from "@/infrastructure/router/StaticImage";
import { artistSupabase } from "@/infrastructure/supabase/client";
import {
	ArtistEdgeFunctionError,
	invokeArtistRoute,
} from "@/infrastructure/supabase/functions";
import {
	beginRegionalWebSignIn,
	clearRegionalAuthCallbackFragment,
	completeRegionalWebSignIn,
	isRegionalAuthProvider,
	readRegionalAuthCallback,
} from "@/infrastructure/supabase/regionalAuth";
import { useArtistSession } from "./ArtistSessionProvider";

type AuthStep = "email" | "password" | "signup-otp";
type EmailIntent = "existing" | "new";

interface ProviderDescriptor {
	readonly key: ArtistAuthProviderKey;
	readonly id: string;
	readonly title: string;
	readonly asset: string;
	readonly scopes?: string;
}

const PROVIDERS: Readonly<Record<ArtistAuthProviderKey, ProviderDescriptor>> = {
	wechat: {
		key: "wechat",
		id: "wechat",
		title: "WeChat",
		asset: staticAssetPath("/images/auth/providers/wechat.png"),
	},
	douyin: {
		key: "douyin",
		id: "douyin",
		title: "Douyin",
		asset: staticAssetPath("/images/auth/providers/douyin.png"),
	},
	qq: {
		key: "qq",
		id: "custom:qq",
		title: "QQ",
		asset: staticAssetPath("/images/auth/providers/qq.png"),
	},
	watcha: {
		key: "watcha",
		id: "custom:watcha",
		title: "观猹",
		asset: staticAssetPath("/images/auth/providers/watcha.png"),
		scopes: "read",
	},
	"linux-do": {
		key: "linux-do",
		id: "custom:linux-do",
		title: "Linux Do",
		asset: staticAssetPath("/images/auth/providers/linux-do.png"),
		scopes: "openid profile email",
	},
	google: {
		key: "google",
		id: "google",
		title: "Google",
		asset: staticAssetPath("/images/auth/providers/google.png"),
	},
	facebook: {
		key: "facebook",
		id: "facebook",
		title: "Facebook",
		asset: staticAssetPath("/images/auth/providers/facebook.png"),
	},
	x: {
		key: "x",
		id: "twitter",
		title: "X",
		asset: staticAssetPath("/images/auth/providers/x.png"),
	},
	apple: {
		key: "apple",
		id: "apple",
		title: "Apple",
		asset: staticAssetPath("/images/auth/providers/apple.png"),
	},
};

const OTP_COOLDOWN_SECONDS = 60;
const MINIMUM_PASSWORD_LENGTH = 8;

export function AuthEntry({
	loader,
}: Readonly<{ loader?: ArtistDomainLoader }>) {
	const router = useRouter();
	const session = useArtistSession();
	const runtimeConfiguration = useArtistRuntimeConfiguration();
	const { t } = useTranslation();
	const emailFieldId = useId();
	const passwordFieldId = useId();
	const otpFieldId = useId();
	const captchaRef = useRef<HCaptcha>(null);
	const regionalCallbackHandledRef = useRef(false);

	const [runtime, setRuntime] = useState<ArtistDomainRuntime | null>(null);
	const [kernelError, setKernelError] = useState(false);
	const [step, setStep] = useState<AuthStep>("email");
	const [intent, setIntent] = useState<EmailIntent | null>(null);
	const [emailAddress, setEmailAddress] = useState("");
	const [canonicalEmail, setCanonicalEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [otpCode, setOtpCode] = useState("");
	const [captchaToken, setCaptchaToken] = useState<string | null>(null);
	const [captchaGeneration, setCaptchaGeneration] = useState(0);
	const [formError, setFormError] = useState<string | null>(null);
	const [statusMessage, setStatusMessage] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [otpCooldown, setOtpCooldown] = useState(0);

	const providers = useMemo(
		() =>
			runtimeConfiguration.authenticationProviderKeys.map(
				(key) => PROVIDERS[key],
			),
		[runtimeConfiguration.authenticationProviderKeys],
	);
	const captchaSiteKey = import.meta.env.VITE_HCAPTCHA_SITE_KEY?.trim() ?? "";

	useEffect(() => {
		let active = true;
		void loadArtistDomain(loader)
			.then((loaded) => {
				if (active) setRuntime(loaded);
			})
			.catch(() => {
				if (active) setKernelError(true);
			});
		return () => {
			active = false;
		};
	}, [loader]);

	useEffect(() => {
		if (session.phase === "user") router.replace("/account");
	}, [router, session.phase]);

	useEffect(() => {
		if (
			runtimeConfiguration.region !== "china" ||
			regionalCallbackHandledRef.current
		) return;
		const callback = readRegionalAuthCallback();
		if (callback === null) return;
		regionalCallbackHandledRef.current = true;
		setSubmitting(true);
		setFormError(null);
		void (async () => {
			try {
				if (callback.kind === "error") throw new Error(callback.error);
				await completeRegionalWebSignIn({
					callback,
					clientRedirectUri: `${window.location.origin}/auth`,
				});
				clearRegionalAuthCallbackFragment();
			} catch {
				clearRegionalAuthCallbackFragment();
				setFormError("区域账户登录未完成，请重试。");
				regionalCallbackHandledRef.current = false;
			} finally {
				setSubmitting(false);
			}
		})();
	}, [runtimeConfiguration.region]);

	useEffect(() => {
		if (otpCooldown <= 0) return;
		const handle = window.setTimeout(() => {
			setOtpCooldown((remaining) => Math.max(0, remaining - 1));
		}, 1_000);
		return () => window.clearTimeout(handle);
	}, [otpCooldown]);

	function resetCaptcha(): void {
		setCaptchaToken(null);
		setCaptchaGeneration((value) => value + 1);
		captchaRef.current?.resetCaptcha();
	}

	async function resolveEmailIntent(
		event: FormEvent<HTMLFormElement>,
	): Promise<void> {
		event.preventDefault();
		setFormError(null);
		setStatusMessage(null);
		if (runtime === null || kernelError) {
			setFormError("认证能力尚未就绪，请稍后再试。");
			return;
		}
		if (captchaSiteKey.length === 0) {
			setFormError("当前部署缺少登录安全验证配置。");
			return;
		}
		if (captchaToken === null) {
			setFormError("请先完成安全验证。");
			return;
		}
		const canonical = runtime.canonicalizeEmail(emailAddress);
		if (canonical.kind === "failure") {
			setFormError(emailFailureMessage(canonical.error.code, t));
			return;
		}

		setSubmitting(true);
		try {
			const result = await invokeArtistRoute<{
				readonly schemaVersion: 1;
				readonly intent: EmailIntent;
			}>("/auth/email-intent", {
				body: JSON.stringify({
					email: canonical.value.canonical,
					captchaToken,
				}),
			});
			setCanonicalEmail(canonical.value.canonical);
			setIntent(result.intent);
			setPassword("");
			setStep("password");
			setStatusMessage(
				result.intent === "existing"
					? "欢迎回来，请输入密码。"
					: "这是一个新账户，请设置密码完成注册。",
			);
			resetCaptcha();
		} catch (error) {
			setFormError(
				edgeErrorMessage(error, "暂时无法确认账户状态，请稍后重试。"),
			);
			resetCaptcha();
		} finally {
			setSubmitting(false);
		}
	}

	async function submitPassword(
		event: FormEvent<HTMLFormElement>,
	): Promise<void> {
		event.preventDefault();
		if (intent === null || submitting) return;
		setFormError(null);
		setStatusMessage(null);
		if (password.length < MINIMUM_PASSWORD_LENGTH) {
			setFormError(`密码至少需要 ${MINIMUM_PASSWORD_LENGTH} 个字符。`);
			return;
		}
		if (captchaToken === null) {
			setFormError("请完成登录安全验证。");
			return;
		}

		setSubmitting(true);
		try {
			const supabase = artistSupabase();
			if (intent === "existing") {
				const { error } = await supabase.auth.signInWithPassword({
					email: canonicalEmail,
					password,
					options: { captchaToken },
				});
				if (error) throw error;
				return;
			}

			const { data, error } = await supabase.auth.signUp({
				email: canonicalEmail,
				password,
				options: {
					emailRedirectTo: `${window.location.origin}/auth`,
					...(captchaToken === null ? {} : { captchaToken }),
				},
			});
			if (error) throw error;
			if (data.session !== null) return;
			setOtpCode("");
			setStep("signup-otp");
			setOtpCooldown(OTP_COOLDOWN_SECONDS);
			setStatusMessage(`验证码已发送至 ${canonicalEmail}`);
			resetCaptcha();
		} catch (error) {
			setFormError(authErrorMessage(error));
			resetCaptcha();
		} finally {
			setSubmitting(false);
		}
	}

	async function verifySignupOtp(
		event: FormEvent<HTMLFormElement>,
	): Promise<void> {
		event.preventDefault();
		if (runtime === null || submitting) return;
		setFormError(null);
		let sanitized: string;
		try {
			sanitized = runtime.sanitizeOtpCode(otpCode);
		} catch {
			setKernelError(true);
			setRuntime(null);
			return;
		}
		if (sanitized.length !== 6) {
			setFormError(t("auth.error.otp.length"));
			return;
		}

		setSubmitting(true);
		try {
			const { error } = await artistSupabase().auth.verifyOtp({
				email: canonicalEmail,
				token: sanitized,
				type: "signup",
			});
			if (error) throw error;
		} catch (error) {
			setFormError(authErrorMessage(error));
		} finally {
			setSubmitting(false);
		}
	}

	async function resendSignupOtp(): Promise<void> {
		if (otpCooldown > 0 || submitting) return;
		setSubmitting(true);
		setFormError(null);
		try {
			const { error } = await artistSupabase().auth.resend({
				type: "signup",
				email: canonicalEmail,
				options: { emailRedirectTo: `${window.location.origin}/auth` },
			});
			if (error) throw error;
			setOtpCooldown(OTP_COOLDOWN_SECONDS);
			setOtpCode("");
			setStatusMessage(`新的验证码已发送至 ${canonicalEmail}`);
		} catch (error) {
			setFormError(authErrorMessage(error));
		} finally {
			setSubmitting(false);
		}
	}

	async function resetPassword(): Promise<void> {
		if (intent !== "existing" || submitting) return;
		setSubmitting(true);
		setFormError(null);
		try {
			const { error } = await artistSupabase().auth.resetPasswordForEmail(
				canonicalEmail,
				{
					redirectTo: `${window.location.origin}/auth?mode=recovery`,
					...(captchaToken === null ? {} : { captchaToken }),
				},
			);
			if (error) throw error;
			setStatusMessage("密码重置邮件已发送，请检查邮箱。");
		} catch (error) {
			setFormError(authErrorMessage(error));
		} finally {
			setSubmitting(false);
		}
	}

	async function signInWithProvider(
		provider: ProviderDescriptor,
	): Promise<void> {
		if (submitting || !session.providerAvailable) return;
		setSubmitting(true);
		setFormError(null);
		try {
			if (
				runtimeConfiguration.region === "china" &&
				isRegionalAuthProvider(provider.key)
			) {
				await beginRegionalWebSignIn({
					region: runtimeConfiguration.region,
					provider: provider.key,
					clientRedirectUri: `${window.location.origin}/auth`,
				});
			}
			type OAuthProvider = Parameters<
				ReturnType<typeof artistSupabase>["auth"]["signInWithOAuth"]
			>[0]["provider"];
			const { error } = await artistSupabase().auth.signInWithOAuth({
				provider: provider.id as OAuthProvider,
				options: {
					redirectTo: `${window.location.origin}/auth`,
					...(provider.scopes === undefined ? {} : { scopes: provider.scopes }),
				},
			});
			if (error) throw error;
		} catch (error) {
			setSubmitting(false);
			setFormError(authErrorMessage(error));
		}
	}

	function returnToEmail(): void {
		setStep("email");
		setIntent(null);
		setPassword("");
		setOtpCode("");
		setStatusMessage(null);
		setFormError(null);
		setOtpCooldown(0);
		resetCaptcha();
	}

	function handleClose(): void {
		if (window.history.length > 1) router.back();
		else router.replace("/discover");
	}

	const title =
		step === "signup-otp"
			? "验证邮箱"
			: step === "password"
				? intent === "existing"
					? "欢迎回来"
					: "创建账户"
				: t("auth.title.welcome");

	return (
		<section className="auth-entry-page" aria-labelledby="auth-entry-title">
			<header className="auth-topbar">
				<GlassIconButton
					label={t("common.close")}
					onClick={handleClose}
					disabled={session.phase === "loading" || submitting}
				>
					<ArtistIcon name="close" size={20} />
				</GlassIconButton>
				<GlassIconButton
					label={t("settings.general.language")}
					onClick={() => router.push("/settings")}
					disabled={session.phase === "loading"}
				>
					<ArtistIcon name="globe" size={20} />
				</GlassIconButton>
			</header>

			<div className="auth-entry__body">
				<div className="auth-branding" aria-live="polite">
					<span className="auth-branding__logo" aria-hidden="true">
						<span className="brand-logo brand-logo--auth" />
					</span>
					<h1 id="auth-entry-title" className="auth-branding__title">
						{title}
					</h1>
					<p className="auth-branding__subtitle">
						{step === "email"
							? runtimeConfiguration.credentialAuthenticationEnabled
								? "使用邮箱或可信账户继续你的创作旅程"
								: "使用可信账户继续你的创作旅程"
							: step === "signup-otp"
								? `输入发送至 ${canonicalEmail} 的 6 位验证码`
								: canonicalEmail}
					</p>
				</div>

				<div className="auth-entry__card-wrap">
					<GlassCard className="auth-entry-card">
						{kernelError || session.phase === "error" ? (
							<div className="auth-domain-error" role="alert">
								<strong>{t("auth.error.kernel")}</strong>
								<span>{session.error ?? "本地规则核心无法验证。"}</span>
								<button type="button" onClick={session.retryRestore}>
									{t("auth.retry.restore")}
								</button>
							</div>
						) : null}

						{statusMessage !== null ? (
							<Callout
								style="info"
								title={t("common.ok")}
								message={statusMessage}
								className="auth-callout"
							/>
						) : null}

						{step === "email" ? (
							<>
								{runtimeConfiguration.credentialAuthenticationEnabled ? (
								<form
									className="auth-credential-form"
									onSubmit={(event) => void resolveEmailIntent(event)}
									noValidate
								>
									<GlassTextField
										label={t("auth.field.email")}
										id={emailFieldId}
										type="email"
										inputMode="email"
										autoComplete="email"
										placeholder="you@example.com"
										value={emailAddress}
										disabled={runtime === null || submitting}
										onChange={(event) => {
											setEmailAddress(event.currentTarget.value);
											setFormError(null);
										}}
										leading={<ArtistIcon name="mail" size={18} />}
									/>
									{captchaSiteKey.length > 0 ? (
										<div
											className="auth-captcha"
											key={`intent-${captchaGeneration}`}
										>
											<HCaptcha
												ref={captchaRef}
												sitekey={captchaSiteKey}
												theme="dark"
												onVerify={setCaptchaToken}
												onExpire={() => setCaptchaToken(null)}
												onError={() => setCaptchaToken(null)}
											/>
										</div>
									) : null}
									<GlassButton
										kind="primary"
										size="lg"
										expands
										isLoading={submitting}
										isEnabled={
											runtime !== null &&
											emailAddress.includes("@") &&
											captchaToken !== null
										}
										symbol={<ArtistIcon name="chevron" size={16} />}
										type="submit"
									>
										继续
									</GlassButton>
								</form>
								) : null}

								<div className="auth-social">
									{runtimeConfiguration.credentialAuthenticationEnabled ? (
									<div className="auth-social-divider" aria-hidden="true">
										<span />
										<span className="auth-social-divider__text">或使用</span>
										<span />
									</div>
									) : null}
									<div className="auth-provider-grid">
										{providers.map((provider) => (
											<button
												key={provider.key}
												type="button"
												className="auth-provider-button"
												disabled={submitting || !session.providerAvailable}
												onClick={() => void signInWithProvider(provider)}
											>
												<img src={provider.asset} alt="" aria-hidden="true" />
												<span>{provider.title}</span>
											</button>
										))}
									</div>
								</div>
							</>
						) : step === "password" ? (
							<form
								className="auth-credential-form"
								onSubmit={(event) => void submitPassword(event)}
								noValidate
							>
								<GlassTextField
									label={intent === "existing" ? "密码" : "设置密码"}
									id={passwordFieldId}
									type={showPassword ? "text" : "password"}
									autoComplete={
										intent === "existing" ? "current-password" : "new-password"
									}
									value={password}
									onChange={(event) => {
										setPassword(event.currentTarget.value);
										setFormError(null);
									}}
									leading={<ArtistIcon name="lock" size={18} />}
								/>
								<label className="auth-show-password">
									<input
										type="checkbox"
										checked={showPassword}
										onChange={(event) =>
											setShowPassword(event.currentTarget.checked)
										}
									/>
									<span>显示密码</span>
								</label>
								{intent === "new" ? (
									<p className="auth-password-hint">
										至少 {MINIMUM_PASSWORD_LENGTH}{" "}
										个字符。验证码仅在创建新账户后发送。
									</p>
								) : null}
								<div
									className="auth-captcha"
									key={`password-${captchaGeneration}`}
								>
									<HCaptcha
										ref={captchaRef}
										sitekey={captchaSiteKey}
										theme="dark"
										onVerify={setCaptchaToken}
										onExpire={() => setCaptchaToken(null)}
										onError={() => setCaptchaToken(null)}
									/>
								</div>
								<GlassButton
									kind="primary"
									size="lg"
									expands
									isLoading={submitting}
									isEnabled={
										password.length >= MINIMUM_PASSWORD_LENGTH &&
										captchaToken !== null
									}
									symbol={<ArtistIcon name="chevron" size={16} />}
									type="submit"
								>
									{intent === "existing" ? "登录" : "创建账户"}
								</GlassButton>
								<div className="auth-otp-secondary">
									{intent === "existing" ? (
										<button
											type="button"
											className="auth-text-button"
											onClick={() => void resetPassword()}
											disabled={submitting}
										>
											忘记密码
										</button>
									) : null}
									<button
										type="button"
										className="auth-text-button auth-text-button--muted"
										onClick={returnToEmail}
										disabled={submitting}
									>
										更换邮箱
									</button>
								</div>
							</form>
						) : (
							<form
								className="auth-otp-form"
								onSubmit={(event) => void verifySignupOtp(event)}
								noValidate
							>
								<GlassTextField
									label={t("auth.field.otp")}
									id={otpFieldId}
									type="text"
									inputMode="numeric"
									autoComplete="one-time-code"
									placeholder="••••••"
									value={otpCode}
									maxLength={6}
									onChange={(event) => {
										setOtpCode(
											event.currentTarget.value
												.replace(/[^0-9]/gu, "")
												.slice(0, 6),
										);
										setFormError(null);
									}}
									aria-invalid={formError !== null || undefined}
									className="auth-otp-input"
								/>
								<GlassButton
									kind="primary"
									size="lg"
									expands
									isLoading={submitting}
									isEnabled={otpCode.length === 6}
									symbol={<ArtistIcon name="check" size={16} />}
									type="submit"
								>
									完成注册
								</GlassButton>
								<div className="auth-otp-secondary">
									<button
										type="button"
										className="auth-text-button"
										onClick={() => void resendSignupOtp()}
										disabled={otpCooldown > 0 || submitting}
									>
										{otpCooldown > 0
											? `${otpCooldown} 秒后重发`
											: "重新发送验证码"}
									</button>
									<button
										type="button"
										className="auth-text-button auth-text-button--muted"
										onClick={returnToEmail}
										disabled={submitting}
									>
										更换邮箱
									</button>
								</div>
							</form>
						)}

						{formError !== null ? (
							<p className="auth-form-error" role="alert">
								{formError}
							</p>
						) : null}
					</GlassCard>
				</div>
			</div>

			<footer
				className="auth-terms"
				role="contentinfo"
				aria-label={t("auth.terms.terms")}
			>
				<p className="auth-terms__prefix">{t("auth.terms.prefix")}</p>
				<p className="auth-terms__links">
					<Link href="/terms" className="auth-terms__link">
						{t("auth.terms.terms")}
					</Link>
					<span className="auth-terms__sep">{t("auth.terms.sep")}</span>
					<Link href="/privacy" className="auth-terms__link">
						{t("auth.terms.privacy")}
					</Link>
				</p>
			</footer>
		</section>
	);
}

function emailFailureMessage(code: string, t: (key: TranslationKey) => string): string {
	switch (code) {
		case "auth.email.empty":
			return t("auth.error.email.empty");
		case "auth.email.too-long":
			return t("auth.error.email.tooLong");
		case "auth.email.invalid-characters":
			return t("auth.error.email.invalidChars");
		default:
			return t("auth.error.email.default");
	}
}

function authErrorMessage(error: unknown): string {
	if (error instanceof Error && error.message.trim().length > 0) {
		const normalized = error.message.toLowerCase();
		if (normalized.includes("invalid login credentials"))
			return "邮箱或密码不正确。";
		if (normalized.includes("email rate limit"))
			return "邮件发送过于频繁，请稍后再试。";
		if (normalized.includes("already registered"))
			return "该邮箱已注册，请返回并使用密码登录。";
		if (normalized.includes("token") || normalized.includes("otp"))
			return "验证码无效或已过期。";
	}
	return "认证暂时无法完成，请稍后重试。";
}

function edgeErrorMessage(error: unknown, fallback: string): string {
	if (error instanceof ArtistEdgeFunctionError) {
		if (error.status === 429) return "尝试次数过多，请稍后再试。";
		if (error.status === 400) return "安全验证失败，请重新完成验证。";
	}
	return fallback;
}
